'use client';

import { cn } from '@/utils/utils_client';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import * as React from 'react';
import { useLayoutEffect } from 'react';

// Extended the primitive props to include the custom thumb class name
type UI_PRIMITIVE__switch_props = React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> & {
  class_name_thumb?: string;
};

export const UI_PRIMITIVE__switch = React.forwardRef<React.ElementRef<typeof SwitchPrimitives.Root>, UI_PRIMITIVE__switch_props>(
  ({ className, class_name_thumb, ...props }, ref) => {
    const ref_root = React.useRef<HTMLButtonElement | null>(null);

    useLayoutEffect(() => {
      const el = ref_root.current;
      if (!el) return;

      const update = (): void => {
        const rect = el.getBoundingClientRect();

        const padding = 4; // p-[2px] both sides
        const thumb_size = rect.height - padding;

        const offset = rect.width - thumb_size - padding;

        el.style.setProperty('--switch-offset', `${offset}px`);
      };

      update();

      const ro = new ResizeObserver(update);
      ro.observe(el);

      return () => ro.disconnect();
    }, []);

    return (
      <SwitchPrimitives.Root
        ref={node => {
          ref_root.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        className={cn(
          `
        peer inline-flex size-full
        shrink-0 cursor-pointer
        rounded-full p-[2px]
        transition-colors
        focus-visible:outline-none
        focus-visible:ring-2
        focus-visible:ring-ring
        focus-visible:ring-offset-2
        focus-visible:ring-offset-background
        disabled:cursor-not-allowed
        disabled:opacity-50
        `,
          'bg-zinc-200 dark:bg-zinc-700 data-[state=checked]:bg-[#34C759]',
          className,
        )}
        {...props}
      >
        <SwitchPrimitives.Thumb
          className={cn(
            `
          pointer-events-none block
          aspect-square h-full
          rounded-full bg-white
          shadow-lg
          transition-transform duration-200
          will-change-transform

          data-[state=checked]:translate-x-[var(--switch-offset)]
          data-[state=unchecked]:translate-x-0
          `,
            class_name_thumb,
          )}
        />
      </SwitchPrimitives.Root>
    );
  },
);

UI_PRIMITIVE__switch.displayName = SwitchPrimitives.Root.displayName;
